/**
 * labTuning.js — TEMPORARY Phase 8 dev-only visual-tuning bridge. SCAFFOLDING ONLY.
 *
 * A module-level mutable store of the LAB particle field's aesthetic levers, dialed live by the
 * dev tuning panel (labTunePanel.js) and read once per frame by Sketch.update() / DarkMatter.update().
 * Mirrors labBackground.js (no React state, stable reference, no per-frame allocation). Defaults
 * EQUAL the current baked-in constants, so the field is byte-identical until a slider moves.
 *
 * REMOVE ENTIRELY at Phase 8 sign-off: bake the chosen values back into their constants/#defines/
 * inline shader math, revert the temporary uniform promotions, then delete this file, labTunePanel.js,
 * and every per-frame push that reads it.
 */
const labTuning = {
    // ── Composition — labCloudHome lobes (BOTH GPGPU shaders; CPU-derived in pushLabComposition) ──
    lobeSpread: 0.62,              // lobe-center distance from screen center (in W/2, H/2 fractions)
    lobeScale: 0.30,               // largest lobe radius (H fractions) — tight lobes = dense cores
    lobeAsym: 0.82,                // geometric radius falloff lobe i -> i+1 (asymmetric sizes)
    lobeZNear: 0.10,               // nearest lobe z (H fractions; camera at +0.875H)
    lobeZSpan: 0.90,               // total z stagger across the 4 lobes (depth layering)
    falloffPow: 2.2,               // radial density gradient (higher = denser core, sparser halo)
    armFrac: 0.18,                 // fraction of DUST re-homed onto lobe->lobe trailing arms
    armBulge: 0.35,                // arm midpoint outward bulge (arcs sweep around the void)
    voidX: 0.32, voidY: 0.20,      // central protection void radii on (W, H) — keep the card clear
    driftAmp: 0.0,                 // lobe-center drift amplitude — OFF until 8R-C (no slider yet)
    driftFreq: 0.015,              // lobe-center drift frequency (8R-C)

    // ── Motion — velocity shader ──
    containment: 0.0050,           // spring strength toward home (structure vs. noise)
    noiseScale: 0.0015,            // curl frequency
    noiseSpeed: 0.05,              // curl time speed
    flowStrength: 1.5,             // curl amplitude (Sketch flow line; base = LAB_FLOW_STRENGTH)

    // ── Lifecycle ──
    maxAge: 3.0,                   // shared lifespan (uLabMaxAge)

    // ── Tier hierarchy — vertex/fragment, LAB-gated (Phase 8R v2) ──
    dustFrac: 0.55,                // fraction of particles that are fine dust
    anchorFrac: 0.02,              // fraction promoted to glowing anchors (~82 of 4096 at 0.02)
    dustSize: 0.75,                // dust size center (+/- 0.2 within-tier jitter)
    medSize: 1.35,                 // medium size center (+/- 0.35) — these form the cloud body
    anchorSize: 2.2,               // anchor size center (+/- 0.55)
    anchorGlow: 0.45,              // anchor emissive during the bloom dark pass (gentle, not blown)

    // ── Look — fragment, LAB-gated (Phase 8R v2) ──
    baseLum: 0.40,                 // base-luminance floor (light-independent visibility); tiers x(0.8, 1.15, 1.6)
    discCore: 0.25,                // soft-disc bright core radius (lower = softer/gaussian-like)
    discEdge: 0.85,                // soft-disc outer edge (fragments beyond are discarded in LAB)
    depthDim: 0.5,                 // far-depth dimming amount (fake DOF)

    // ── Render — LAB-gated mixes in Sketch (Home byte-identical at uLabBlend 0) ──
    particleScale: 1.0,            // LAB particle scale target
    lightIntensity: 2.0,           // LAB PointLight intensity target (Home base = 2.0)
    bloomStrength: 1.0,            // factor on the bloom-strength drive
    bloomRadius: 0.6,              // LAB bloom radius (softness) — decoupled from strength; v2: softer halos
    bloomThreshold: 0.35,          // LAB bloom threshold (what blooms)
    pColorValue: 0.5,              // LAB particle monochrome grey value

    // ── Dev-only UI visibility toggle (NOT a tuning value, NOT baked) ──
    hideLabUi: false,              // when true, the LAB foreground UI is hidden so only the field shows
};

/** Stable read-only snapshot (same object every call). */
export function getLabTuning() { return labTuning; }

export default labTuning;
